import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Shield, Clock, UserCheck, CheckCircle, ChevronRight } from "lucide-react";

const steps = [
  { label: "APPLY", active: true },
  { label: "REVIEW", active: false },
  { label: "APPROVE", active: false },
  { label: "FUND", active: false },
];

const trustItems = [
  { icon: <Shield className="w-5 h-5" />, title: "No Credit Impact", desc: "Applying will not affect your credit score" },
  { icon: <Clock className="w-5 h-5" />, title: "24-Hour Decisions", desc: "Fast turnaround on all applications" },
  { icon: <UserCheck className="w-5 h-5" />, title: "Dedicated Concierge", desc: "White-glove service from start to finish" },
];

const services = [
  { title: "Business Term Loans", desc: "Flexible terms from $25K to $5M with competitive rates tailored to your business profile.", amount: "Up to $5M" },
  { title: "Revenue-Based Financing", desc: "Funding based on your revenue with payments that adjust to your cash flow.", amount: "Up to $1M" },
  { title: "Equipment Financing", desc: "Acquire the equipment you need with structures designed for optimal tax efficiency.", amount: "Up to $2M" },
  { title: "Line of Credit", desc: "Revolving credit facility providing capital exactly when you need it most.", amount: "Up to $500K" },
];

export default function HomePage() {
  return (
    <div data-testid="home-page" className="bg-[#0a0e1a]">
      {/* Hero */}
      <section className="hero-premium relative overflow-hidden min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Ambient light effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[radial-gradient(ellipse,rgba(212,175,55,0.06)_0%,transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-[radial-gradient(ellipse,rgba(37,99,235,0.05)_0%,transparent_70%)] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 text-amber-400 text-xs font-medium tracking-wider uppercase mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Exclusive Funding Solutions
              </div>

              <h1 data-testid="hero-heading" className="mb-6">
                <span className="block text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] tracking-tight">
                  Premium Financing
                </span>
                <span className="block text-4xl sm:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-300 leading-[1.08] tracking-tight mt-1">
                  Made Simple
                </span>
              </h1>

              <p data-testid="hero-subtitle" className="text-base md:text-lg text-slate-400 max-w-lg leading-relaxed mb-10">
                Exclusive funding solutions for discerning clients. Experience white-glove service
                with our streamlined application process. No credit impact for applying.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/apply">
                  <Button
                    data-testid="hero-apply-btn"
                    size="lg"
                    className="bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold px-8 h-12 text-base cta-gold shadow-[0_0_24px_rgba(212,175,55,0.25)] hover:shadow-[0_0_32px_rgba(212,175,55,0.35)] transition-all"
                  >
                    Apply Now <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <a href="#services">
                  <Button
                    data-testid="hero-learn-btn"
                    size="lg"
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 hover:bg-amber-500/5 px-8 h-12 text-base bg-transparent"
                  >
                    Learn More
                  </Button>
                </a>
              </div>
            </div>

            {/* Right: Status Card */}
            <div className="relative z-10 flex justify-center lg:justify-end">
              <Card
                data-testid="hero-status-card"
                className="w-full max-w-sm border-slate-700/50 bg-[#111827]/80 backdrop-blur-xl shadow-2xl shadow-black/40"
              >
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <p className="text-xs text-slate-500 uppercase tracking-widest font-medium mb-2">
                      Pre-Qualified Amount
                    </p>
                    <p className="text-5xl font-extrabold text-white tracking-tight">
                      $250<span className="text-amber-400">,</span>000
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full h-1.5 rounded-full bg-slate-700 mb-6 overflow-hidden">
                    <div className="h-full w-1/4 rounded-full bg-gradient-to-r from-amber-500 to-amber-400" />
                  </div>

                  {/* Steps */}
                  <div className="flex items-center justify-between">
                    {steps.map((step, i) => (
                      <div key={step.label} className="flex items-center">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                              step.active
                                ? "bg-amber-500 text-[#0a0e1a]"
                                : "bg-slate-700 text-slate-400"
                            }`}
                          >
                            {step.active ? <CheckCircle className="w-4 h-4" /> : i + 1}
                          </div>
                          <span
                            className={`text-[10px] mt-1.5 font-semibold tracking-wider ${
                              step.active ? "text-amber-400" : "text-slate-500"
                            }`}
                          >
                            {step.label}
                          </span>
                        </div>
                        {i < steps.length - 1 && (
                          <ChevronRight className="w-3 h-3 text-slate-600 mx-1 mb-4" />
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-5 border-t border-slate-700/50">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Status</span>
                      <span className="text-emerald-400 font-medium flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        Ready to Apply
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section data-testid="trust-section" className="border-t border-slate-800 bg-[#0d1220]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trustItems.map((item, i) => (
              <div
                key={i}
                data-testid={`trust-item-${i}`}
                className="flex items-start gap-4 p-5 rounded-xl border border-slate-800 bg-slate-800/20"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-0.5">{item.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" data-testid="services-section" className="bg-[#0a0e1a] py-16 md:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs text-amber-400 uppercase tracking-widest font-medium mb-3">Our Solutions</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Tailored Funding Products
            </h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">
              Comprehensive financing options designed for established businesses seeking growth capital.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <Card
                key={i}
                data-testid={`service-card-${i}`}
                className="border-slate-800 bg-[#111827]/60 hover:border-amber-500/30 transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <p className="text-xs text-amber-400 font-semibold tracking-wider uppercase mb-3">{s.amount}</p>
                  <h3 className="text-base font-semibold text-white mb-2 group-hover:text-amber-300 transition-colors">{s.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section data-testid="process-section" className="bg-[#0d1220] py-16 md:py-20 px-4 border-t border-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs text-amber-400 uppercase tracking-widest font-medium mb-3">How It Works</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Simple, Streamlined Process
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: "01", title: "Submit Application", desc: "Complete our secure online application in under 5 minutes." },
              { num: "02", title: "Expert Review", desc: "Our concierge team reviews and structures your funding." },
              { num: "03", title: "Receive Capital", desc: "Approved funds deposited directly to your account." },
            ].map((step, i) => (
              <div key={i} className="text-center" data-testid={`process-step-${i}`}>
                <div className="text-5xl font-extrabold text-slate-800 mb-3">{step.num}</div>
                <h3 className="text-base font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-16 md:py-20 px-4 bg-[#0a0e1a] border-t border-slate-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Secure Your Funding?
          </h2>
          <p className="text-base text-slate-400 mb-8 max-w-lg mx-auto">
            Join thousands of businesses that trust EasyQuickFund for premium financing solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/apply">
              <Button
                data-testid="cta-apply-btn"
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold px-10 h-12 cta-gold shadow-[0_0_24px_rgba(212,175,55,0.2)]"
              >
                Start Your Application <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/become-partner">
              <Button
                data-testid="cta-partner-btn"
                size="lg"
                variant="outline"
                className="border-slate-600 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 hover:bg-amber-500/5 px-10 h-12 bg-transparent"
              >
                Become a Partner
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
